/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

import React, { useState } from 'react';
import PropTypes from "prop-types";
import { useRouteMatch } from 'react-router';
import { OuiImage } from '../../../../src/components/image';
import { OuiButton } from '../../../../src/components/button';
import { OuiSpacer } from '../../../../src/components/spacer';
import { OuiSwitch } from '../../../../src/components/form';
import { OuiTextAlign } from '../../../../src/components/text';
import { useIsWithinBreakpoints } from '../../../../src/services/hooks';
import { useExitPath } from '../../services/routing/routing';
import contentSvg from '../../images/content.svg';
import contentCenterSvg from '../../images/content_center.svg';
import sideNavSvg from '../../images/side_nav.svg';
import singleSvg from '../../images/single.svg';
var ExitFullscreenDemoButton = function ExitFullscreenDemoButton() {
  var exitPath = useExitPath();
  return /*#__PURE__*/React.createElement(OuiButton, {
    fill: true,
    href: exitPath,
    iconType: "exit"
  }, "Exit full screen");
};

/**
 * Because the discrete[by slug] usages of <PageDemo> are not rendered at the same time,
 * it is safe to cache their showTemplate status in a set rather than an approach triggering
 * React re-rendering. If that changes, moving this to a redux store, recoiljs atom(s), or similar
 * would resolve any introduced issue.
 */
var demosAsIndividualComponents = new Set();
export var PageDemo = ({
  slug,
  fullscreen,
  pattern,
  template,
  centered
}) => {
  const {
    path
  } = useRouteMatch();
  const isMobileSize = useIsWithinBreakpoints(['xs', 's']);
  const [showTemplate, _setShowTemplate] = useState(!demosAsIndividualComponents.has(slug));
  const setShowTemplate = cb => {
    _setShowTemplate(showing => {
      const nextValue = cb(showing);
      demosAsIndividualComponents[nextValue ? 'delete' : 'add'](slug);
      return nextValue;
    });
  };
  const button = fullscreen ? <ExitFullscreenDemoButton /> : <OuiButton fill href={`#${path}/${slug}`}>
      Go full screen
    </OuiButton>;
  const sideNav = <OuiImage size={isMobileSize ? 'original' : 'fullWidth'} alt="Fake side nav list" url={isMobileSize ? singleSvg : sideNavSvg} />;
  const content = <>
      <OuiImage size="fullWidth" alt="Fake paragraph" url={centered ? contentCenterSvg : contentSvg} />
      {!centered && <>
          <OuiSpacer />
          <OuiImage size="fullWidth" alt="Fake paragraph" url={centered ? contentCenterSvg : contentSvg} />
        </>}
    </>;
  const bottomBar = <OuiButton size="s" color="ghost">
      Save
    </OuiButton>;
  const Child = showTemplate ? template : pattern;
  return fullscreen ? <Child button={button} content={content} sideNav={sideNav} bottomBar={bottomBar} /> : <>
      <div className={'guideDemo__highlightLayout'}>
        <Child button={button} content={content} sideNav={sideNav} bottomBar={bottomBar} />
      </div>
      <OuiTextAlign textAlign="right">
        <OuiSpacer />
        <OuiSwitch label="Show with individual components" checked={!showTemplate} onChange={() => setShowTemplate(showing => !showing)} />
      </OuiTextAlign>
    </>;
};
PageDemo.propTypes = {
  slug: PropTypes.string.isRequired,
  fullscreen: PropTypes.bool,
  pattern: PropTypes.elementType.isRequired,
  template: PropTypes.elementType.isRequired,
  centered: PropTypes.bool
};
try {
  PageDemo.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src-docs/src/views/page/_page_demo.tsx',
    description: '',
    displayName: 'PageDemo',
    methods: [],
    props: {
      slug: {
        defaultValue: null,
        description: '',
        name: 'slug',
        parent: undefined,
        declarations: [{
          fileName: 'src/src-docs/src/views/page/_page_demo.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      fullscreen: {
        defaultValue: null,
        description: '',
        name: 'fullscreen',
        parent: undefined,
        declarations: [{
          fileName: 'src/src-docs/src/views/page/_page_demo.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      pattern: {
        defaultValue: null,
        description: '',
        name: 'pattern',
        parent: undefined,
        declarations: [{
          fileName: 'src/src-docs/src/views/page/_page_demo.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ComponentType<{ button: ReactElement<any, string | JSXElementConstructor<any>>; content: ReactElement<any, string | JSXElementConstructor<any>>; sideNav: ReactElement<...>; bottomBar: ReactElement<...>; }>'
        }
      },
      template: {
        defaultValue: null,
        description: '',
        name: 'template',
        parent: undefined,
        declarations: [{
          fileName: 'src/src-docs/src/views/page/_page_demo.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ComponentType<{ button: ReactElement<any, string | JSXElementConstructor<any>>; content: ReactElement<any, string | JSXElementConstructor<any>>; sideNav: ReactElement<...>; bottomBar: ReactElement<...>; }>'
        }
      },
      centered: {
        defaultValue: null,
        description: '',
        name: 'centered',
        parent: undefined,
        declarations: [{
          fileName: 'src/src-docs/src/views/page/_page_demo.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}